import React from "react";
import {connect} from "react-redux";
import {updatePage} from "../../actions/page";
import AccessGate from "../../components/permissions/AccessGate";
import {convertBytes, displayErrors} from "../../libs/utils";
import {Card, Col, message, Modal, Row, Space, Table, Tabs, Tag, Tooltip} from "antd";
import Button from "antd/es/button";
import {DEFAULT_DOMAINS_PER_PAGE} from "../../config";
import {
    loadWRMProject,
    loadWRMProjectAssets,
    loadWRMProjectURLs, restartWRMProject,
    updateWRMProject, uploadWRMProject
} from "../../actions/wayback_machine_restoration";
import {DateTimeFormat} from "../../components/shared/DateTimeFormat";
import {StatusIndicator} from "../../components/shared/Status";
import {
    CheckCircleOutlined,
    CloseCircleOutlined, CloudUploadOutlined,
    ExclamationCircleOutlined,
    EyeOutlined,
    StopOutlined, SyncOutlined
} from "@ant-design/icons";
import {DateFormat} from "../../components/shared/DateFormat";
import {Loading} from "../../libs/loading";
import UndoOutlined from "@ant-design/icons/lib/icons/UndoOutlined";
import RetweetOutlined from "@ant-design/icons/lib/icons/RetweetOutlined";
import {HiOutlineExternalLink} from "react-icons/hi";

class WaybackMachineRestorationProject extends React.Component {
    state = {
        data: null,
        urls: [],
        assets: [],
        loading: true,
        loadingUrls: false,
        loadingAssets: false,
        pagination: { current: 1, pageSize: DEFAULT_DOMAINS_PER_PAGE, showSizeChanger: true, total: 0 },
        restartingProcess: false,
        stoppingProcess: false,
        uploadingSource: false,
    };

    componentDidMount() {
        this.loadData();
    }

    loadData() {
        this.loadProject();
        this.loadUrls();
        this.loadAssets();
    }

    loadProject() {
        this.setState({ loading: true });

        this.props.loadWRMProject(this.props.match.params.guid, (res) => {
            this.props.updatePage(<span>{res.data.domain.domain} <Tag color='warning'>Beta</Tag></span>);

            this.setState({ data: res.data, loading: false });
        }, (err) => {
            if(typeof err.response !== 'undefined') {
                this.setState({ loading: false });
                displayErrors(err.response.data);
            }
        })
    }

    loadUrls() {
        this.setState({ loadingUrls: true });

        this.props.loadWRMProjectURLs(this.props.match.params.guid, (res) => {
            this.setState({ urls: res.data, loadingUrls: false });
        }, (err) => {
            if(typeof err.response !== 'undefined') {
                this.setState({ loadingUrls: false });
                displayErrors(err.response.data);
            }
        })
    }

    loadAssets() {
        this.setState({ loadingAssets: true });

        this.props.loadWRMProjectAssets(this.props.match.params.guid, (res) => {
            this.setState({ assets: res.data, loadingAssets: false });
        }, (err) => {
            if(typeof err.response !== 'undefined') {
                this.setState({ loadingAssets: false });
                displayErrors(err.response.data);
            }
        })
    }


    onTabChange(value) {
        if(value === 'urls') {
            this.loadUrls();
        } else if(value === 'assets') {
            this.loadAssets();
        }
    }

    stopProcess() {
        const { confirm } = Modal;

        confirm({
            title: 'Attention',
            centered: true,
            okText: 'Stop',
            okType: 'danger',
            okButtonProps: {
                loading: this.state.stoppingProcess
            },
            icon: <ExclamationCircleOutlined />,
            content: <span>Are you sure you wish to stop the restoration process?</span>,
            onOk: () => {
                this.setState({ stoppingProcess: true });

                this.props.cancelWRMProject(this.props.match.params.guid, () => {
                    message.success('Restoration process successfully scheduled for stopping.');
                    this.loadProject();
                    this.setState({ stoppingProcess: false });
                }, (err) => {
                    if(typeof err.response !== 'undefined') {
                        displayErrors(err.response.data);
                        this.setState({ stoppingProcess: false });
                    }
                });
            }
        });
    }

    restartProcess() {
        const { confirm } = Modal;

        confirm({
            title: 'Attention',
            centered: true,
            okText: 'Restart',
            okType: 'danger',
            okButtonProps: {
                loading: this.state.restartingProcess
            },
            icon: <ExclamationCircleOutlined />,
            content: <span>Are you sure you wish to restart the restoration process?</span>,
            onOk: () => {
                this.setState({ restartingProcess: true });

                this.props.restartWRMProject(this.props.match.params.guid, () => {
                    message.success('Restoration process successfully restarted.');
                    this.loadProject();
                    this.loadUrls();
                    this.loadAssets();
                    this.setState({ restartingProcess: false });
                }, (err) => {
                    if(typeof err.response !== 'undefined') {
                        displayErrors(err.response.data);
                        this.setState({ restartingProcess: false });
                    }
                });
            }
        });
    }

    uploadSource() {
        const { confirm } = Modal;

        confirm({
            title: 'Attention',
            centered: true,
            okText: 'Upload',
            okType: 'danger',
            okButtonProps: {
                loading: this.state.uploadingSource
            },
            icon: <ExclamationCircleOutlined />,
            content: <span>Are you sure you wish to upload the source?</span>,
            onOk: () => {
                this.setState({ uploadingSource: true });

                this.props.uploadWRMProject(this.props.match.params.guid, () => {
                    message.success('Source successfully uploaded!');
                    this.setState({ uploadingSource: false });
                }, (err) => {
                    if(typeof err.response !== 'undefined') {
                        displayErrors(err.response.data);
                        this.setState({ uploadingSource: false });
                    }
                });
            }
        });
    }

    render() {
        const columns = [
            { title: 'Domain', dataIndex: 'domain', width: '30%', render: (item) => <a target='_blank' rel='noreferrer noopener' href={'https://' + item.domain}>{item.domain} <HiOutlineExternalLink style={{marginBottom: '-3px', marginRight: '5px'}} /></a> },
            { title: 'Approximate Date', align: 'center', dataIndex: 'approximate_date', render: (item) => item === null ? '-' :  <DateFormat value={item} /> },
            { title: 'Status', align: 'center', dataIndex: 'status', render: (item) => <StatusIndicator el='badge' status={item} /> },
            { title: 'Created', align: 'center', dataIndex: 'date_created', render: (item) => <DateTimeFormat value={item} /> },
            { title: 'Completed', align: 'center', dataIndex: 'date_completed', render: (item) => item === null ? '-' : <DateTimeFormat value={item} /> },
            { title: 'URLs', children: [
                { title: 'Discovered', align: 'center', dataIndex: 'discovered_urls'},
                { title: 'Crawled', align: 'center', dataIndex: 'crawled_urls'},
                { title: 'Failed', align: 'center', dataIndex: 'failed_urls'},
            ]},
            { title: 'Assets', children: [
                { title: 'Discovered', align: 'center', dataIndex: 'discovered_assets'},
                { title: 'Crawled', align: 'center', dataIndex: 'crawled_assets'},
                { title: 'Failed', align: 'center', dataIndex: 'failed_assets'},
            ]}
        ];

        const urlsColumns = [
            { title: 'URL', width: '50%', render: (item, record) => {
                    return <div>
                        <div className='wayback-machine-restoration-url'>
                            <a href={'http://' + record.url} target='_blank' rel='noopener noreferrer'>{record.url}</a><br/>
                        </div>
                        <div className='wayback-machine-restoration-url'>
                        <a className='wayback-machine-restoration-archive-url' href={record.archive_url} target='_blank'
                               rel='noopener noreferrer'>{record.archive_url}</a>
                        </div>
                    </div>
                }
            },
            {title: 'Discovered', dataIndex: 'date_created', render: (item) => <DateTimeFormat value={item}/>},
            {title: 'Crawled', dataIndex: 'date_crawled', render: (item) => item === null ? '-' : <DateTimeFormat value={item} /> },
            { title: 'Processed', align: 'center', dataIndex: 'is_processed', render: (item, record) => {
                    if(record.status === 'PENDING' || record.status === 'CRAWLING') {
                        return <SyncOutlined spin />;
                    } else {
                        if(item) {
                            return <CheckCircleOutlined style={{color: 'green'}} />;
                        } else {
                            return <CloseCircleOutlined style={{color: 'red'}} />;
                        }
                    }
            }},
            { title: 'Valid', align: 'center', dataIndex: 'is_valid_url', render: (item, record) => {
                    if(record.status === 'PENDING' || record.status === 'CRAWLING') {
                        return <SyncOutlined spin />;
                    } else {
                        if(item) {
                            return <CheckCircleOutlined style={{color: 'green'}} />;
                        } else {
                            return <CloseCircleOutlined style={{color: 'red'}} />;
                        }
                    }
            }},
            { title: 'Status', align: 'center', dataIndex: 'status', render: (item) =>  <StatusIndicator el='badge' status={item} />},
            { title: 'Size', align: 'center', dataIndex: 'file_size', render: (item) => item === null ? '-' : convertBytes(item) },
            { title: '', dataIndex: 'archive_url', align: 'right', width: '150px', render: (item) => <a href={item} target='_blank' rel='noopener noreferrer'><Button icon={<EyeOutlined />} size='small'>Archived Page</Button></a> },
        ];

        const assetsColumns = [
            { title: 'URL', width: '50%', render: (item, record) => {
                    return <div>
                        <div className='wayback-machine-restoration-url'>
                            <a href={'http://' + record.url} target='_blank' rel='noopener noreferrer'>{record.url}</a><br/>
                        </div>
                        <div className='wayback-machine-restoration-url'>
                            <a className='wayback-machine-restoration-archive-url' href={record.archive_url} target='_blank'
                               rel='noopener noreferrer'>{record.archive_url}</a>
                        </div>
                    </div>
                }
            },
            {title: 'Discovered', dataIndex: 'date_created', render: (item) => <DateTimeFormat value={item}/>},
            {title: 'Crawled', dataIndex: 'date_crawled', render: (item) => item === null ? '-' : <DateTimeFormat value={item} /> },
            { title: 'Processed', align: 'center', dataIndex: 'is_processed', render: (item, record) => {
                if(record.status === 'PENDING' || record.status === 'CRAWLING') {
                    return <SyncOutlined spin />;
                } else {
                    if(item) {
                        return <CheckCircleOutlined style={{color: 'green'}} />;
                    } else {
                        return <CloseCircleOutlined style={{color: 'red'}} />;
                    }
                }
            }},
            { title: 'Valid', align: 'center', dataIndex: 'is_valid_asset', render: (item, record) => {
                    if(record.status === 'PENDING' || record.status === 'CRAWLING') {
                        return <SyncOutlined spin />;
                    } else {
                        if(item) {
                            return <CheckCircleOutlined style={{color: 'green'}} />;
                        } else {
                            return <CloseCircleOutlined style={{color: 'red'}} />;
                        }
                    }
            }},
            { title: 'Status', align: 'center', dataIndex: 'status', render: (item) => <StatusIndicator el='badge' status={item} /> },
            { title: 'Size', align: 'center', dataIndex: 'file_size', render: (item) => item === null ? '-' : convertBytes(item) },
            { title: '', dataIndex: 'archive_url', align: 'right', width: '150px', render: (item) => <a href={item} target='_blank' rel='noopener noreferrer'>
                    <Button icon={<EyeOutlined />} size='small'>Archived Page</Button>
            </a> },
        ];

        const tabs = [
            { label: 'URLs', key: 'urls', children: <div>
                    <Row justify='space-between' style={{paddingBottom: '10px'}}>
                        <Col />
                        <Col>
                            <Tooltip title='Reload table'>
                                <Button type='text' icon={<UndoOutlined />} onClick={() => this.loadUrls()}/>
                            </Tooltip>
                        </Col>
                    </Row>
                    <div style={{overflowX: 'auto'}}>
                        <Table
                            size='small'
                            dataSource={this.state.urls}
                            loading={this.state.loadingUrls}
                            columns={urlsColumns}
                            rowKey={(item) => item.url} />
                    </div>
                </div> },

            { label: 'Assets', key: 'assets', children: <div>
                    <Row justify='space-between' style={{paddingBottom: '10px'}}>
                        <Col />
                        <Col>
                            <Tooltip title='Reload table'>
                                <Button type='text' icon={<UndoOutlined />} onClick={() => this.loadAssets()}/>
                            </Tooltip>
                        </Col>
                    </Row>
                    <div style={{overflowX: 'auto'}}>
                        <Table
                            size='small'
                            loading={this.state.loadingAssets}
                            dataSource={this.state.assets}
                            columns={assetsColumns}
                            rowKey={(item) => item.url} />
                    </div>
                </div> },
        ];

        if(this.state.data === null) {
            return <Loading />;
        }

        return <AccessGate scopes={['can_view_wayback_machine_restoration']}>
            <Card size='small'>
                <Row justify='space-between' style={{paddingBottom: '10px'}}>
                    <Col>
                        <Space>
                            {this.state.data.status === 'ACTIVE' ? <Button icon={<StopOutlined />} onClick={() => this.stopProcess()}>Stop Process</Button> : ''}
                            {this.state.data.status === 'COMPLETED' ? <Button icon={<RetweetOutlined />} onClick={() => this.restartProcess()}>Restart Process</Button> : ''}
                            {this.state.data.status === 'COMPLETED' && this.state.data.date_cleanup === null ? <Button loading={this.state.uploadingSource} disabled={this.state.uploadingSource} icon={<CloudUploadOutlined />} onClick={() => this.uploadSource()}>Upload Source</Button> : ''}
                        </Space>
                    </Col>
                    <Col>
                        <Tooltip title='Reload table'>
                            <Button type='text' icon={<UndoOutlined/>} onClick={() => this.loadData()}/>
                        </Tooltip>
                    </Col>
                </Row>
                <div style={{overflowX: 'auto'}}>
                    <Table
                        size='small'
                        dataSource={[this.state.data]}
                        loading={this.state.loading}
                        columns={columns} bordered
                        rowKey={(item) => item.guid}
                        pagination={false}/>
                </div>
            </Card>
            <br/>
            <Card size='small'>
                <Tabs onChange={(value) => this.onTabChange(value)} items={tabs} />
            </Card>
        </AccessGate>;
    }
}

export default connect(null, {
    updatePage, loadWRMProject, loadWRMProjectURLs, loadWRMProjectAssets, updateWRMProject, uploadWRMProject,
    restartWRMProject
})(WaybackMachineRestorationProject);